import { AxiosResponse } from 'axios'
import axiosInstance, { isAxiosErrorResponse } from './api'
import { PostAnswerData } from '../models/global.types'
import config from '../config/config'
import { PreferenceType } from '../enums/enums'

export const signIn = async (email: string, password: string, clientId: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/oauth/token`, {
  grant_type: 'password',
  client_id: clientId,
  username: email,
  password
})

/**
 * @param {boolean} skipStatusValidation - Manually create the request and skip Axios interceptors
 */
export const checkUserStatus = async (
  email: string,
  skipStatusValidation: boolean = false): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    // Skip response interceptors
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/auth/status?email=${email}`,
      method: 'get',
      validateStatus: null
    })
  }

  return await axiosInstance.get(`/v${config.apiVersion}/auth/status?email=${email}`)
}

export const beginUserActivation = async (email: string, suppressToast: boolean = false): Promise<AxiosResponse> => {
  const response = await axiosInstance.post(`/v${config.apiVersion}/auth/activation/initiate`, { email }, { suppressToast } as any)
  return response
}

export const checkUserActivation = async (code: string): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/auth/activation/validate?code=${code}`)

export const completeUserActivation = async (payload: any): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/activation/complete`, payload)

export const confirmUserActivation = async (): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/activation/confirm`, undefined, { skipAuthRedirect: true, suppressToast: true } as any)

export const fetchCurrentAttempt = async (): Promise<AxiosResponse> => await axiosInstance.get('/v2/quiz/attempts/current', {})

export const fetchAttemptSessions = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions`, {})

export const fetchQuestions = async (attemptId: string, sessionId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v2/quiz/attempts/${attemptId}/sessions/${sessionId}/questions`)

export const getSessionAnswers = async (attemptId: string, sessionId: string, questionId: string, answers: any): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/answers`, {
  params: {
    answers: [{
      question_id: questionId,
      answers
    }]
  }
})

export const postSessionAnswers = async (attemptId: string, sessionId: string, answers: PostAnswerData[], updateAttemptScore?: boolean, suppressToast?: boolean): Promise<AxiosResponse> => {
  return await axiosInstance.post(
    `/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/answers`,
    { answers },
    {
      params: { updateAttemptScore },
      suppressToast
    } as any
  )
}

export const startAttempt = async (): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts`)

export const getAttempts = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts`)

export const completeAttempt = async (attemptId: string, suppressToast?: boolean): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts/${attemptId}/complete`, {}, { suppressToast } as any)

export const fetchModules = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/modules`)

export const getCurrentSession = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v2/quiz/attempts/${attemptId}/sessions/current`)

export const startNextSession = async (attemptId: string, suppressToast?: boolean): Promise<AxiosResponse> => await axiosInstance.post(`/v2/quiz/attempts/${attemptId}/sessions/next`, {}, { suppressToast } as any)

export const completeSession = async (attemptId: string, sessionId: string, suppressToast?: boolean): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/complete`, {}, { suppressToast } as any)

export const getCurrentAttemptAndSession = async (): Promise<AxiosResponse> => await axiosInstance.get('/v2/quiz/attempts/current-attempt-and-session')

export const getUserProfile = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/profile/me`)

export const getAccount = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/accounts/me`)

export const updateUserProfile = async (payload: any): Promise<AxiosResponse> => await axiosInstance.put(`/v${config.apiVersion}/profile/me`, payload)

export const changePassword = async (payload: any): Promise<AxiosResponse> => await axiosInstance.put(`/v${config.apiVersion}/profile/password/change`, payload)

export const signInUser = async (email: string, password: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/users/signin-user`, { email, password })

export const createUserPerception = async (moduleId: string, action: string, value: number): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/user-perception/${moduleId}/${action}/${value}`, { moduleId, action, value })

export const getUsers = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/users`)

export const getUsersWithAttempts = async (active?: boolean, status?: string): Promise<AxiosResponse> => {
  const queryParams = []

  if (active === true) {
    queryParams.push(`active=${active.toString()}`)
  }

  if (typeof status === 'string' && status?.length > 0) {
    queryParams.push(`status=${encodeURIComponent(status)}`)
  }

  const queryParamString = queryParams.length > 0 ? '?' + queryParams.join('&') : ''
  return await axiosInstance.get(`/v${config.apiVersion}/users/with-attempts${queryParamString}`)
}

export const processUserFileUpload = async (file: File): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('students', file)

  return await axiosInstance.post(`/v${config.apiVersion}/users/invites/uploads`, formData)
}

export const getUserPreferences = async (skipStatusValidation: boolean = true): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/users/get-user-preferences`,
      method: 'get',
      validateStatus: null
    })
  }

  return await axiosInstance.get(`/v${config.apiVersion}/users/get-user-preferences`)
}

export const setUserPreference = async (
  preferenceType: PreferenceType,
  value: string,
  skipStatusValidation: boolean = true
): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/users/set-user-preference`,
      method: 'post',
      data: { preferenceType, value },
      validateStatus: null
    })
  }

  return await axiosInstance.post(
    `/v${config.apiVersion}/users/set-user-preference`,
    {
      preferenceType,
      value
    }
  )
}

// SSO Authentication
export const validateSSOToken = async (oktaToken: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/sso/validate`, { oktaToken })

// SAML2 Authentication
export interface ValidateSAMLResponseRequest {
  samlResponse: string
}

export interface ValidateSAMLResponseResponse {
  data: {
    accessToken: string
  }
}

export const validateSAMLResponse = async (
  data: ValidateSAMLResponseRequest
): Promise<AxiosResponse<ValidateSAMLResponseResponse>> => {
  return await axiosInstance.post(`/v${config.apiVersion}/auth/saml/validate`, data)
}

// Survey
export const getSurveyByUserGeneratedId = async (userGeneratedId: string): Promise<AxiosResponse> =>
  await axiosInstance.get(`/v${config.apiVersion}/survey/by-user-generated-id/${userGeneratedId}`)

const getSurveyStartErrorMessage = (res: AxiosResponse | null | undefined): string => {
  const description = res?.data?.error_description
  if (typeof description === 'string' && description !== '') {
    return description
  }
  return 'Failed to start survey'
}

export const startSurvey = async (surveyId: string, anonTag: string): Promise<AxiosResponse> => {
  if (typeof anonTag !== 'string' || anonTag.trim() === '') {
    throw new Error('anonTag is required')
  }

  const res = await axiosInstance.post(
    `/v${config.apiVersion}/survey/${surveyId}/start`,
    { anonTag },
    { headers: { 'Content-Type': 'application/json' } }
  )

  if (isAxiosErrorResponse(res)) {
    throw new Error(getSurveyStartErrorMessage(res))
  }

  return res
}

export const getSurveyWithProgress = async (surveyId: string, responseId: string): Promise<AxiosResponse> =>
  await axiosInstance.get(`/v${config.apiVersion}/survey/${surveyId}/response/${responseId}`)

export const submitSurveyAnswer = async (
  surveyId: string,
  responseId: string,
  questionId: string,
  data: { textResponse?: string, choiceIds?: string[] }
): Promise<AxiosResponse> =>
  await axiosInstance.put(
    `/v${config.apiVersion}/survey/${surveyId}/response/${responseId}/questions/${questionId}/answer`,
    data
  )

export const completeSurvey = async (surveyId: string, responseId: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/survey/${surveyId}/response/${responseId}/complete`)

export const sendPhoneLoginReminder = async (email: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/auth/send-phone-login-reminder`, { email })

// Naloxone survey - QR path, no sign-in. Account comes from the Account header (hostname-derived).
export const getNaloxoneSurvey = async (): Promise<AxiosResponse> =>
  await axiosInstance.get(`/v${config.apiVersion}/naloxone-survey`)

export const startNaloxoneSurvey = async (): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/naloxone-survey/start`)

export const submitNaloxoneAnswer = async (
  responseId: string,
  questionId: string,
  data: { textResponse?: string, choiceIds?: string[] }
): Promise<AxiosResponse> =>
  await axiosInstance.put(
    `/v${config.apiVersion}/naloxone-survey/response/${responseId}/questions/${questionId}/answer`,
    data
  )

export const setNaloxoneAnonTag = async (responseId: string, anonTag: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/naloxone-survey/response/${responseId}/anon-tag`, { anonTag })

export const completeNaloxoneSurvey = async (responseId: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/naloxone-survey/response/${responseId}/complete`)

const apis = {
  checkUserStatus,
  beginUserActivation,
  checkUserActivation,
  completeUserActivation,
  confirmUserActivation,
  signIn,
  fetchCurrentAttempt,
  fetchAttemptSessions,
  fetchQuestions,
  getSessionAnswers,
  postSessionAnswers,
  startAttempt,
  getAttempts,
  getCurrentAttemptAndSession,
  completeAttempt,
  fetchModules,
  getCurrentSession,
  startNextSession,
  completeSession,
  getUserProfile,
  updateUserProfile,
  changePassword,
  signInUser,
  getAccount,
  createUserPerception,
  getUsers,
  getUsersWithAttempts,
  getUserPreferences,
  setUserPreference,
  validateSSOToken,
  validateSAMLResponse,
  getSurveyByUserGeneratedId,
  startSurvey,
  getSurveyWithProgress,
  submitSurveyAnswer,
  completeSurvey,
  sendPhoneLoginReminder,
  getNaloxoneSurvey,
  startNaloxoneSurvey,
  submitNaloxoneAnswer,
  setNaloxoneAnonTag,
  completeNaloxoneSurvey
}

export default apis
